export interface ConversionResult {
    success: boolean;
    originalUrl: string;
    originalParams: Record<string, any>;
    designEndpointUrl: string;
    designEndpointBody: object;
    endpointType: 'composeDesign' | 'autoLayout' | 'composeDynamicLine' | 'composition:generate' | 'adjustForSurface';
    error?: string;
}

/**
 * Build the POST body for /api/v3/design/{designJob}.
 * designJob is in the path and auth is a JWT, so both designJob and apiKey are dropped.
 */
export function buildDesignPostBody(requestBody: Record<string, any>): Record<string, any> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { designJob, apiKey, ...body } = requestBody;
    return body;
}

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/** POST responses wrap the document in { result: { cimpressDocuments: [doc] } }; GET returns it bare. */
export function extractDesignDocument(data: any): any {
    return data?.result ? data.result.cimpressDocuments?.[0] : data;
}

/**
 * Parse and type-convert URL query parameters
 * Handles: booleans, numbers, JSON strings, duplicate keys (as arrays), and bracket notation (as objects)
 */
function parseQueryParams(params: URLSearchParams): Record<string, any> {
    const parsed: Record<string, any> = {};

    // Get all keys to detect duplicates and bracket notation
    const allValues: Record<string, any[]> = {};
    params.forEach((value, key) => {
        if (!allValues[key]) {
            allValues[key] = [];
        }
        allValues[key].push(value);
    });

    // Separate bracket notation keys from regular keys
    const bracketKeys: Record<string, Record<string, any[]>> = {};
    const regularKeys: string[] = [];

    Object.keys(allValues).forEach((key) => {
        // Check for bracket notation: key[subkey]
        // Using .+? (non-greedy) to match content between brackets
        const bracketMatch = key.match(/^([^[]+)\[(.+?)\]$/);
        if (bracketMatch) {
            const [, baseKey, subKey] = bracketMatch;
            if (!bracketKeys[baseKey]) {
                bracketKeys[baseKey] = {};
            }
            if (!bracketKeys[baseKey][subKey]) {
                bracketKeys[baseKey][subKey] = [];
            }
            bracketKeys[baseKey][subKey].push(...allValues[key]);
        } else {
            regularKeys.push(key);
        }
    });

    // Helper function to convert a value
    const convertValue = (value: string): any => {
        // Convert booleans
        if (value === 'true') return true;
        if (value === 'false') return false;
        // Convert numbers
        if (!Number.isNaN(Number(value)) && value.trim() !== '') return Number(value);
        // Try to parse JSON
        try {
            return JSON.parse(value);
        } catch {
            return value;
        }
    };

    // Process bracket notation keys - create objects
    Object.keys(bracketKeys).forEach((baseKey) => {
        const subKeys = bracketKeys[baseKey];
        const obj: Record<string, any> = {};

        Object.keys(subKeys).forEach((subKey) => {
            const values = subKeys[subKey];
            const value = values.length > 1 ? values.map(convertValue) : convertValue(values[0]);
            // Nested bracket keys, e.g. product[options][Stock] -> subKey "options][Stock"
            const segments = subKey.split('][');
            if (segments.some((segment) => UNSAFE_KEYS.has(segment))) {
                return;
            }
            let target = obj;
            segments.slice(0, -1).forEach((segment) => {
                if (typeof target[segment] !== 'object' || target[segment] === null) {
                    target[segment] = {};
                }
                target = target[segment];
            });
            target[segments[segments.length - 1]] = value;
        });

        parsed[baseKey] = obj;
    });

    // Process regular keys
    regularKeys.forEach((key) => {
        const values = allValues[key];

        // If multiple values, treat as array
        if (values.length > 1) {
            parsed[key] = values.map(convertValue);
        } else {
            parsed[key] = convertValue(values[0]);
        }
    });

    return parsed;
}

/**
 * Helper: Build target product section (common across all endpoints)
 */
function buildTargetProduct(params: Record<string, any>): Record<string, any> {
    const targetProduct: Record<string, any> = {};

    // Note: productSpec and surfaceSpecificationUrl are mutually exclusive
    if (params.surfaceSpecificationUrl) {
        targetProduct.surfaceSpecificationUrl = params.surfaceSpecificationUrl;
    } else if (params.productId || params.productVersion || params.productOptions) {
        targetProduct.productSpec = {
            ...(params.productId && { id: params.productId }),
            ...(params.productVersion && { version: params.productVersion }),
            ...(params.productOptions && { options: params.productOptions }),
        };
    }

    if (params.substrateColor) {
        targetProduct.substrateColor = params.substrateColor;
    }

    if (params.productName) {
        targetProduct.productName = params.productName;
    }

    return targetProduct;
}

/**
 * Helper: Transform image assets by mapping 'purpose' to 'context'
 */
function transformImageAssets(assets: any): any {
    if (!assets || !assets.images || !Array.isArray(assets.images) || assets.images.length === 0) {
        return assets;
    }

    const transformedColorPalette: { brandColors: { color: string; context: string }[] } = {
        brandColors: [],
    };

    const colorPalette = assets?.colorPalette;

    if (colorPalette?.brandColors?.length) {
        transformedColorPalette.brandColors = colorPalette.brandColors;
    }
    if (colorPalette?.primary) {
        transformedColorPalette.brandColors.push({ color: colorPalette.primary, context: 'primary' });
    }
    if (colorPalette?.secondary) {
        transformedColorPalette.brandColors.push({ color: colorPalette.secondary, context: 'secondary' });
    }
    if (colorPalette?.neutral) {
        transformedColorPalette.brandColors.push({ color: colorPalette.neutral, context: 'neutral' });
    }
    if (colorPalette?.accent) {
        transformedColorPalette.brandColors.push({ color: colorPalette.accent, context: 'accent' });
    }
    if (colorPalette?.background) {
        transformedColorPalette.brandColors.push({ color: colorPalette.background, context: 'background' });
    }

    return {
        ...assets,
        images: assets.images.map((img: { context?: string; purpose?: string }) => ({
            ...img,
            context: img.context ?? img.purpose,
            purpose: undefined,
        })),
        colorPalette: transformedColorPalette.brandColors.length ? transformedColorPalette : undefined,
    };
}

/**
 * Helper: Build design personalization context array
 */
function buildDesignPersonalizationContext(params: Record<string, any>): any[] {
    const dpcArray: any[] = [];

    if (params.designPersonalizationContextId) {
        dpcArray.push(params.designPersonalizationContextId);
    }

    if (params.dpcs) {
        if (Array.isArray(params.dpcs)) {
            dpcArray.push(...params.dpcs);
        } else {
            dpcArray.push(params.dpcs);
        }
    }

    return dpcArray;
}

/**
 * Helper: Build personal data section (common across all endpoints)
 */
function buildPersonalData(params: Record<string, any>): Record<string, any> {
    const personalData: Record<string, any> = {};

    if (params.assets) {
        personalData.assets = transformImageAssets(params.assets);
    }

    if (params.brandProfileUrl || params.designPersonalizationContextId || params.dpcs || params.sourceDocumentUrl) {
        personalData.brand = {
            ...(params.brandProfileUrl && { brandProfileUrl: params.brandProfileUrl }),
            ...(params.sourceDocumentUrl && { sourceDocumentUrl: params.sourceDocumentUrl }),
        };

        const dpcArray = buildDesignPersonalizationContext(params);
        if (dpcArray.length > 0) {
            personalData.brand.designPersonalizationContext = dpcArray;
        }
    }

    return personalData;
}

/**
 * Helper: Build audience section (common across all endpoints)
 */
function buildAudience(params: Record<string, any>): Record<string, any> | undefined {
    if (params.culture) {
        return { culture: params.culture };
    }
    return undefined;
}

/**
 * Helper: Build feature flags (common across all endpoints)
 */
function applyFeatureFlags(body: any, params: Record<string, any>): void {
    if (params.featureFlags) {
        body.featureFlags = params.featureFlags;
    }
    if (params.experimentalFlags) {
        body.experimentalFlags = params.experimentalFlags;
    }
}

/**
 * Convert composeDesign URL to /api/v3/design request body
 */
function convertComposeDesign(params: Record<string, any>, apiKey: string): object {
    const body: any = {
        designJob: 'PersonalizeDesign',
        targetProduct: buildTargetProduct(params),
        personalData: buildPersonalData(params),
        startingDesign: {},
        apiKey,
    };

    // Starting Design (specific to composeDesign)
    if (params.templateId) {
        body.startingDesign.templateToken = params.templateId;
    }
    if (params.documentUrl) {
        body.startingDesign.documentUrl = params.documentUrl;
    }
    if (params.document) {
        body.startingDesign.document = params.document;
    }

    // Use Case
    if (params.deliveryMode) {
        body.useCase = { documentMode: params.deliveryMode };
    }

    // Audience
    const audience = buildAudience(params);
    if (audience) {
        body.audience = audience;
    }

    // Constraints
    if (params.priority) {
        body.constraints = { priority: params.priority };
    }

    // Feature flags
    applyFeatureFlags(body, params);

    return body;
}

/**
 * Convert autoLayout URL to /api/v3/design request body
 */
function convertAutoLayout(params: Record<string, any>, apiKey: string): object {
    const body: any = {
        designJob: 'PersonalizeDesign',
        targetProduct: buildTargetProduct(params),
        personalData: buildPersonalData(params),
        startingDesign: {},
        apiKey,
    };

    // Starting Design (specific to autoLayout)
    if (params.conceptKitId) {
        body.startingDesign.conceptKitId = params.conceptKitId;
    }

    // Creative Direction (specific to autoLayout)
    if (params.colorVariantId || params.layoutId) {
        body.creativeDirection = {};
        if (params.colorVariantId) {
            body.creativeDirection.referenceStyle = { colorVariantId: params.colorVariantId };
        }
        if (params.layoutId) {
            body.creativeDirection.referenceLayout = { layoutId: params.layoutId };
        }
    }

    // Use Case
    if (params.useCaseId || params.deliveryMode) {
        body.useCase = {
            ...(params.useCaseId && { useCaseId: params.useCaseId }),
            ...(params.deliveryMode && { documentMode: params.deliveryMode }),
        };
    }

    // Audience
    const audience = buildAudience(params);
    if (audience) {
        body.audience = audience;
    }

    // Constraints
    if (params.priority) {
        body.constraints = { priority: params.priority };
    }

    // Feature flags and metadata
    applyFeatureFlags(body, params);
    if (params.documentSourceMetadata) {
        body.documentSourceMetadata = params.documentSourceMetadata;
    }

    return body;
}

/**
 * Convert composeDynamicLine URL to /api/v3/design request body
 */
function convertComposeDynamicLine(params: Record<string, any>, apiKey: string): object {
    const body: any = {
        designJob: 'PersonalizeDesign',
        targetProduct: buildTargetProduct(params),
        personalData: buildPersonalData(params),
        startingDesign: {},
        apiKey,
    };

    // Starting Design (specific to composeDynamicLine)
    if (params.dynamicLineId) {
        body.startingDesign.dynamicLineId = params.dynamicLineId;
    }

    // Creative Direction (specific to composeDynamicLine)
    if (params.colorVariantId) {
        body.creativeDirection = {
            referenceStyle: { colorVariantId: params.colorVariantId },
        };
    }

    // Use Case
    if (params.useCaseId || params.deliveryMode) {
        body.useCase = {
            ...(params.useCaseId && { useCaseId: params.useCaseId }),
            ...(params.deliveryMode && { documentMode: params.deliveryMode }),
        };
    }

    // Audience
    const audience = buildAudience(params);
    if (audience) {
        body.audience = audience;
    }

    // Constraints
    if (params.priority) {
        body.constraints = { priority: params.priority };
    }

    // Feature flags
    applyFeatureFlags(body, params);

    return body;
}

/**
 * Convert composition:generate URL to /api/v3/design request body
 */
function convertCompositionGenerate(params: Record<string, any>, apiKey: string): object {
    const body: any = {
        designJob: 'GenerateDesign',
        targetProduct: buildTargetProduct(params),
        personalData: buildPersonalData(params),
        apiKey,
    };

    // Constraints (specific to composition:generate with additional fields)
    if (params.dpiThreshold || params.priority || params.dynamicLineFilter) {
        body.constraints = {
            ...(params.dpiThreshold && { dpiThreshold: params.dpiThreshold }),
            ...(params.priority && { priority: params.priority }),
            ...(params.dynamicLineFilter && { dynamicLineFilter: params.dynamicLineFilter }),
        };
    }

    // Use Case (specific to composition:generate)
    if (params.panels) {
        body.useCase = { panels: params.panels };
    }

    // Audience
    const audience = buildAudience(params);
    if (audience) {
        body.audience = audience;
    }

    // Feature flags
    applyFeatureFlags(body, params);

    return body;
}

/**
 * Convert adaptation:adjustForSurface URL to an AdaptToProductSurface design job body.
 * Throws for legacy inputs the design job cannot accept.
 */
function convertAdjustForSurface(
    params: Record<string, any>,
    rawSubstrateColor: string | null,
    apiKey: string,
): object {
    const targetProduct: Record<string, any> = {};
    const product = params.product ?? {};
    const productId = product.id ?? params.productId;
    const productVersion = product.version ?? params.productVersion;
    const productOptions = product.options ?? params.productOptions;

    if (params.surfaceSpecificationUrl) {
        targetProduct.surfaceSpecificationUrl = params.surfaceSpecificationUrl;
    } else if (params.surfaceSpecification) {
        throw new Error('Inline surfaceSpecification is not supported by AdaptToProductSurface');
    } else if (productId || productVersion || productOptions) {
        targetProduct.productSpec = {
            ...(productId && { id: productId }),
            ...(productVersion && { version: productVersion }),
            // Options must be strings; parsed bracket params may be numbers or booleans
            ...(productOptions && {
                options: Object.fromEntries(Object.entries(productOptions).map(([k, v]) => [k, String(v)])),
            }),
        };
    }
    if (!targetProduct.surfaceSpecificationUrl && !targetProduct.productSpec) {
        throw new Error('surfaceSpecificationUrl or product (id, version) is required');
    }
    // Raw value: parsed params would turn e.g. "000000" into the number 0
    if (rawSubstrateColor) {
        targetProduct.substrateColor = rawSubstrateColor;
    }

    if (!params.documentUrl && !params.document) {
        throw new Error('documentUrl or document is required');
    }
    // The design job accepts exactly one of documentUrl or document
    const startingDesign = params.documentUrl ? { documentUrl: params.documentUrl } : { document: params.document };

    const body: any = {
        designJob: 'AdaptToProductSurface',
        targetProduct,
        startingDesign,
        apiKey,
    };

    if (params.contentAlignment) {
        body.jobRequirements = { contentAlignment: params.contentAlignment };
    }
    if (params.priority) {
        body.constraints = { priority: params.priority };
    }

    const audience = buildAudience(params);
    if (audience) {
        body.audience = audience;
    }

    applyFeatureFlags(body, params);

    return body;
}

/** Use the playground key for artwork-generation documents, including nested legacy calls. */
function updateDocumentUrlApiKey(documentUrl: string, apiKey: string): string {
    let url: URL;
    try {
        url = new URL(documentUrl);
    } catch {
        return documentUrl;
    }
    if (url.hostname !== 'artworkgeneration.cimpress.io' && url.hostname !== 'dev.artworkgeneration.cimpress.io') {
        return documentUrl;
    }

    url.searchParams.set('apiKey', apiKey);
    const nestedDocumentUrl = url.searchParams.get('documentUrl');
    if (nestedDocumentUrl) {
        url.searchParams.set('documentUrl', updateDocumentUrlApiKey(nestedDocumentUrl, apiKey));
    }
    return url.toString();
}

/**
 * Main conversion function - auto-detects endpoint type and converts to /api/v3/design format
 */
export function convertToDesignEndpoint(
    inputUrl: string,
    backendServiceUrl: string = '',
    fallbackApiKey: string = '',
): ConversionResult {
    if (!inputUrl || !inputUrl.trim()) {
        return {
            success: false,
            originalUrl: '',
            originalParams: {},
            designEndpointUrl: '',
            designEndpointBody: {},
            endpointType: 'composeDesign',
            error: 'Please enter a URL',
        };
    }

    let url: URL;
    try {
        // If it's a path-only URL (starts with /), prepend a dummy host
        if (inputUrl.trim().startsWith('/')) {
            const urlString = backendServiceUrl
                ? `${backendServiceUrl}${inputUrl.trim()}`
                : `https://artworkgeneration.cimpress.io${inputUrl.trim()}`;
            url = new URL(urlString);
        } else {
            url = new URL(inputUrl.trim());
        }
    } catch (e) {
        return {
            success: false,
            originalUrl: inputUrl,
            originalParams: {},
            designEndpointUrl: '',
            designEndpointBody: {},
            endpointType: 'composeDesign',
            error: 'Invalid URL format. Please enter a valid URL or endpoint path.',
        };
    }

    // Sample URLs may omit apiKey; use the caller's key for both legacy and design calls
    if (!url.searchParams.get('apiKey') && fallbackApiKey) {
        url.searchParams.set('apiKey', fallbackApiKey);
    }

    // Normalize before parsing so the legacy URL and design POST body use the same document URL.
    const documentUrl = url.searchParams.get('documentUrl');
    const documentApiKey = fallbackApiKey || url.searchParams.get('apiKey');
    if (documentUrl && documentApiKey) {
        url.searchParams.set('documentUrl', updateDocumentUrlApiKey(documentUrl, documentApiKey));
    }

    const path = url.pathname;
    const params = parseQueryParams(url.searchParams);

    // Extract API key
    const apiKey = params.apiKey || '';
    if (!apiKey) {
        return {
            success: false,
            originalUrl: inputUrl,
            originalParams: params,
            designEndpointUrl: '',
            designEndpointBody: {},
            endpointType: 'composeDesign',
            error: 'API key is required in the URL query parameters (apiKey=...)',
        };
    }

    // Detect endpoint type and convert
    let endpointType: ConversionResult['endpointType'];
    let designEndpointBody: object;

    if (path.includes('/composeDesign')) {
        endpointType = 'composeDesign';
        designEndpointBody = convertComposeDesign(params, apiKey);
    } else if (path.includes('/autoLayout')) {
        endpointType = 'autoLayout';
        designEndpointBody = convertAutoLayout(params, apiKey);
    } else if (path.includes('/composeDynamicLine')) {
        endpointType = 'composeDynamicLine';
        designEndpointBody = convertComposeDynamicLine(params, apiKey);
    } else if (path.includes('/composition:generate')) {
        endpointType = 'composition:generate';
        designEndpointBody = convertCompositionGenerate(params, apiKey);
    } else if (path.toLowerCase().includes('/adaptation:adjustforsurface')) {
        endpointType = 'adjustForSurface';
        try {
            designEndpointBody = convertAdjustForSurface(params, url.searchParams.get('targetSubstrateColor'), apiKey);
        } catch (e) {
            return {
                success: false,
                originalUrl: inputUrl,
                originalParams: params,
                designEndpointUrl: '',
                designEndpointBody: {},
                endpointType: 'adjustForSurface',
                error: (e as Error).message,
            };
        }
    } else {
        return {
            success: false,
            originalUrl: inputUrl,
            originalParams: params,
            designEndpointUrl: '',
            designEndpointBody: {},
            endpointType: 'composeDesign',
            error: 'Unsupported endpoint. Supported: /api/v3/composeDesign, /api/v3/autoLayout, /api/v3/composeDynamicLine, /api/v4/composition:generate, /api/v3/adaptation:adjustForSurface',
        };
    }

    // Construct full original URL
    const originalUrl = url.toString();

    // Extract designJob from the body to construct the path
    const designJob = (designEndpointBody as any).designJob || 'PersonalizeDesign';

    // Construct design endpoint URL with designJob as path parameter
    const designEndpointUrl = backendServiceUrl
        ? `${backendServiceUrl}/api/v3/design/${designJob}`
        : `${url.protocol}//${url.host}/api/v3/design/${designJob}`;

    return {
        success: true,
        originalUrl,
        originalParams: params,
        designEndpointUrl,
        designEndpointBody,
        endpointType,
    };
}
